import React, { useState, useEffect } from "react";
import DatePicker, {
  CalendarContainer,
  registerLocale,
} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ptBR from "date-fns/locale/pt-BR";
import moment from "moment";
import "moment/locale/pt-br";
import {
  DatePickerContainer,
  HeaderContainer,
  YearSelector,
  MonthSelector,
  CustomButton,
  ButtonGroup,
  ButtonArrow,
  ButtonClear,
} from "./style";
import classNames from "classnames";
import { Input } from "reactstrap";
import { maskPeriodo } from "../../../../util/masks";

registerLocale("pt-BR", ptBR);
moment.locale("pt-br");

const CustomDatePicker = ({
  onChangeFiltros,
  startDate,
  endDate,
  accessorStart = "dataInicio",
  accessorEnd = "dataFim",
}) => {
  const [startDateState, setStartDateState] = useState(
    startDate ? moment(startDate).toDate() : null
  );
  const [endDateState, setEndDateState] = useState(
    endDate ? moment(endDate).toDate() : null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [datePickerKey, setDatePickerKey] = useState(Date.now());

  const currentYear = new Date().getFullYear();
  const minYear = 1900;
  const minDate = new Date(minYear, 0, 1);
  const maxDate = new Date(currentYear, 11, 31);

  const years = Array.from(
    { length: currentYear - minYear + 1 },
    (_, i) => minYear + i
  );
  const months = moment.months();

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDateState(start);
    setEndDateState(end);

    onChangeFiltros(
      start && moment(start).isValid()
        ? moment(start).format("YYYY-MM-DD")
        : "",
      accessorStart
    );
    onChangeFiltros(
      end && moment(end).isValid() ? moment(end).format("YYYY-MM-DD") : "",
      accessorEnd
    );
  };

  const handleClear = () => {
    setStartDateState(null);
    setEndDateState(null);
    onChangeFiltros("", accessorStart);
    onChangeFiltros("", accessorEnd);
    setDatePickerKey(Date.now());
  };

const handlePeriodoPreDefinido = (offset) => {
  const targetMonth = moment().subtract(offset, "months");
  const inicio = targetMonth.startOf("month").toDate();
  const fim =
    offset === 0 ? new Date() : targetMonth.endOf("month").toDate();

  setStartDateState(inicio);
  setEndDateState(fim);

  onChangeFiltros(moment(inicio).format("YYYY-MM-DD"), accessorStart);
  onChangeFiltros(moment(fim).format("YYYY-MM-DD"), accessorEnd);
};


  useEffect(() => {
    setStartDateState(startDate ? moment(startDate).toDate() : null);
    setEndDateState(endDate ? moment(endDate).toDate() : null);
  }, [startDate, endDate]);

  return (
    <DatePickerContainer isOpen={isOpen}>
      <DatePicker
        key={datePickerKey}
        locale="pt-BR"
        open={isOpen}
        selected={startDateState || endDateState}
        startDate={startDateState}
        endDate={endDateState}
        onChange={handleDateChange}
        dateFormat="dd/MM/yyyy"
        placeholderText="dd/mm/aaaa - dd/mm/aaaa"
        selectsRange
        minDate={minDate}
        maxDate={maxDate}
        onClickOutside={() => setIsOpen(false)}
        shouldCloseOnSelect={false}
        onChangeRaw={(e) => {
          const rawValue = e?.target?.value || "";
          const maskedValue = maskPeriodo(rawValue);

          e.target.value = maskedValue;

          const parts = maskedValue.split("-");
          if (parts.length === 2) {
            const start = moment(parts[0].trim(), "DD/MM/YYYY", true);
            const end = moment(parts[1].trim(), "DD/MM/YYYY", true);
            if (start.isValid() && end.isValid()) {
              setStartDateState(start.toDate());
              setEndDateState(end.toDate());
              onChangeFiltros(start.format("YYYY-MM-DD"), accessorStart);
              onChangeFiltros(end.format("YYYY-MM-DD"), accessorEnd);
            }
          }
        }}
        renderCustomHeader={({ date, changeYear, changeMonth }) => {
          const displayedMonth = moment(date).month();
          const displayedYear = moment(date).year();

          const prevDate = moment(date).subtract(1, "month").toDate();
          const nextDate = moment(date).add(1, "month").toDate();

          const disablePrev = prevDate < minDate;
          const disableNext = nextDate > maxDate;

          return (
            <HeaderContainer>
              <ButtonArrow
                onClick={() => {
                  if (!disablePrev) {
                    const newDate = moment(date).subtract(1, "month");
                    changeMonth(newDate.month());
                    changeYear(newDate.year());
                  }
                }}
                disabled={disablePrev}
              >
                {"<"}
              </ButtonArrow>

              <YearSelector
                value={displayedYear}
                onChange={({ target: { value } }) => {
                  const year = parseInt(value, 10);
                  changeYear(year);
                }}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </YearSelector>

              <MonthSelector
                value={displayedMonth}
                onChange={({ target: { value } }) => {
                  const month = parseInt(value, 10);
                  changeMonth(month);
                }}
              >
                {months.map((month, index) => (
                  <option key={index} value={index}>
                    {month.charAt(0).toUpperCase() + month.slice(1)}
                  </option>
                ))}
              </MonthSelector>

              <ButtonArrow
                onClick={() => {
                  if (!disableNext) {
                    const newDate = moment(date).add(1, "month");
                    changeMonth(newDate.month());
                    changeYear(newDate.year());
                  }
                }}
                disabled={disableNext}
              >
                {">"}
              </ButtonArrow>
            </HeaderContainer>
          );
        }}
        onInputClick={() => {
          setIsOpen(!isOpen);
        }}
        customInput={<Input style={{ width: "100%", height: "35px" }} />}
        calendarContainer={({ className, children }) => (
          <CalendarContainer
            className={classNames(className, "d-flex flex-row-reverse")}
          >
            <div style={{ position: "relative" }}>{children}</div>
            <ButtonGroup>
              <CustomButton onClick={() => handlePeriodoPreDefinido(0)}>
                {months[moment().month()].charAt(0).toUpperCase() +
                  months[moment().month()].slice(1)}
              </CustomButton>
              <CustomButton onClick={() => handlePeriodoPreDefinido(1)}>
                {months[moment().subtract(1, "month").month()]
                  .charAt(0)
                  .toUpperCase() +
                  months[moment().subtract(1, "month").month()].slice(1)}
              </CustomButton>
              <CustomButton onClick={() => handlePeriodoPreDefinido(2)}>
                {months[moment().subtract(2, "month").month()]
                  .charAt(0)
                  .toUpperCase() +
                  months[moment().subtract(2, "month").month()].slice(1)}
              </CustomButton>
              <ButtonClear onClick={handleClear}>Limpar</ButtonClear>
            </ButtonGroup>
          </CalendarContainer>
        )}
      />
    </DatePickerContainer>
  );
};

export default CustomDatePicker;
